import {
  useUser,
  UserProps,
} from "@customer-tools-web/component-authentication";
import { useBraze, Footer } from "@customer-tools-web/component-header-footer";
import { useI18N } from "@customer-tools-web/component-i18n";
import { useGA } from "@customer-tools-web/google-analysis-tracking";
import { ApolloError } from "apollo-client";
import { isEmpty, noop } from "lodash";
import { parse } from "querystring";
import * as React from "react";
import { useLocation } from "react-router";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import styled, { css } from "styled-components";

import ErrorBoundary from "./components/common/ErrorBoundary";
import ServerErrorModal from "./components/common/ServerErrorModal";
import { ERROR_CODE, ROUTE_URL, URL_PREFIX } from "./components/common/const";
import { useMainContentRef } from "./components/common/hooks/custom-hooks";
import NotFoundPage from "./components/component-error-page/NotFoundPage";
import HeaderComponent from "./components/component-header";
import { RouteType, RouteWithSubRoutes } from "./routing/RouteWithSubRoutes";
import { routeConfig } from "./routing/routeConfig";
import { matchRoutes } from "./routing/routeConfigTools";
import { HiveUiWidgetGlobalStyle } from "@customer-tools-web/component-ui";

function App() {
  const mainContentRef = useMainContentRef();
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const user = useUser();
  const { triggerEvent } = useGA();
  const { t } = useI18N();
  const location = useLocation();
  const braze = useBraze();

  React.useEffect(() => {
    trackPageView(location);
  }, [location.pathname]);

  const isListingsV3Enabled = React.useMemo(() => {
    return user.featureToggle.isListingManagementV3WebEnabled;
  }, [user]);

  return (
    <ErrorBoundary
      render={() => renderContent(user)}
      renderErrorPage={(error) => renderErrorPage(error)}
    />
  );

  function trackPageView(location: RouteComponentProps["location"]) {
    triggerEvent({
      event: "virtual_page_view",
      virtual_page_url: location.pathname + location.search,
    });
  }

  /**
   * agencyQuery seems to return 200 for an error type response and
   * hence errorBoundary doesn't redirect for enquiry-settings.
   */
  function renderContent(user: UserProps["viewer"]) {
    const canViewDashboard: boolean =
      user?.permissions?.dashboard?.canViewDashboard;
    const canViewEnquirySettings: boolean =
      user?.permissions?.editOrganisationEnquiries;

    if (
      isListingsV3Enabled &&
      !canViewDashboard &&
      location.pathname === URL_PREFIX + "/"
    ) {
      window.location.replace(URL_PREFIX + "/v2/listings");
      return null;
    }

    return (
      <>
        {showHeaderFooter() ? (
          <HeaderComponent
            shouldUseRouter
            recordHeaderHeight={(height) => {
              setHeaderHeight(height);
            }}
          />
        ) : null}
        <Content
          onClick={noop}
          headerHeight={headerHeight}
          ref={mainContentRef}
        >
          <Switch>
            {canViewDashboard ? (
              <Redirect
                exact
                from={URL_PREFIX + "/"}
                to={ROUTE_URL.dashboard.default}
              />
            ) : (
              <Redirect exact from={URL_PREFIX + "/"} to={ROUTE_URL.listings} />
            )}
            <Redirect
              exact
              from={ROUTE_URL.addListing.root}
              to={ROUTE_URL.addListing.firstStep}
            />
            <Redirect
              exact
              from={ROUTE_URL.enquiryManagement.root}
              to={ROUTE_URL.enquiryManagement.overView}
            />
            {routeConfig.map((route: RouteType) => {
              // redirect enquiry-settings
              if (
                route?.name === "enquirySettings" &&
                !canViewEnquirySettings
              ) {
                return (
                  <Redirect
                    key={route.path}
                    exact
                    from={route.path}
                    to={`/${URL_PREFIX}/users/expiry`}
                  />
                );
              }
              return (
                // pass braze manually here because the hook is in www project and cannot access by Notification in header-footer component
                <RouteWithSubRoutes
                  key={route.path}
                  {...route}
                  braze={route?.name === "notifications" ? braze : undefined}
                />
              );
            })}
            {!matchRoutes(routeConfig, location.pathname) && (
              <NotFoundPage withoutFooter={!showHeaderFooter()} />
            )}
          </Switch>
          <ServerErrorModal />
          <HiveUiWidgetGlobalStyle />
        </Content>
        {renderFooter()}
      </>
    );
  }
  function showHeaderFooter() {
    const isMobileAuthenticatedPage = /mobile-auth/.test(location.pathname);
    const locationSearch =
      location.search.indexOf("?") === 0
        ? location.search.substring(1)
        : location.search;
    const queryParams = parse(locationSearch);
    const isBrickz = location.pathname.includes("/brickz");
    const isIQAgent = location.pathname.includes("/iq-agent");
    if (
      queryParams.showHeader === "false" ||
      isMobileAuthenticatedPage ||
      isBrickz ||
      isIQAgent
    ) {
      return false;
    }
    return true;
  }
  function renderFooter() {
    const needFooterPaths = [
      URL_PREFIX + "/",
      ROUTE_URL.listings,
      URL_PREFIX + "/listing/:tab(overview|reporting)/:id(\\d+)",
      ROUTE_URL.agencyBranch.list,
      ROUTE_URL.reporting,
      ROUTE_URL.plans.subscription,
      ROUTE_URL.plans.allocation,
      ROUTE_URL.plans.creditUsage,
      ROUTE_URL.batchUpload,
      ROUTE_URL.enquirySettings,
      ROUTE_URL.allocations.default,
      ROUTE_URL.allocations.allocation,
      ROUTE_URL.allocations.individual + "/:orgId",
      ROUTE_URL.allocations.report + "/:orgId",
      ROUTE_URL.watermark,
      ROUTE_URL.leads.leadList,
      ROUTE_URL.agentools.default,
      ROUTE_URL.propertyWanted.default,
      ROUTE_URL.fsbo.default,
      ROUTE_URL.marketAnalytics.landing,
      ROUTE_URL.marketAnalytics.tabs,
      ROUTE_URL.marketWatch.landing,
      ROUTE_URL.dashboard.default,
      ROUTE_URL.dashboard.announcements,
      ROUTE_URL.dashboard.announcements + "/:id",
      ROUTE_URL.scheduledManager.default,
      ROUTE_URL.dashboard.allocation,
      ROUTE_URL.dashboard.subscription,
      ROUTE_URL.announcements.root,
      ROUTE_URL.announcements.preview + "/:id",
      ROUTE_URL.notifications,
      ROUTE_URL.reportings.specialist,
      ROUTE_URL.creditBrickz,
      ROUTE_URL.paymentResult,
      ROUTE_URL.subscriptionRenewal,
      ROUTE_URL.leads.v2.list,
      ROUTE_URL.areaCondoSpecialist.upgradeListing,
    ];

    if (user.permissions.isAgent) {
      needFooterPaths.push(ROUTE_URL.areaCondoSpecialist.default);
      needFooterPaths.push(ROUTE_URL.areaCondoSpecialist.create);
    }
    return (
      <>
        {showHeaderFooter()
          ? needFooterPaths.map((path) => (
              <Route exact key={path} path={path} render={() => <Footer />} />
            ))
          : null}
      </>
    );
  }
  function renderErrorPage(error: ApolloError) {
    let errorMessage, errorPrompt;
    if (
      error &&
      !isEmpty(error.graphQLErrors) &&
      error.graphQLErrors[0].statusCode === ERROR_CODE.notFound
    ) {
      errorMessage = t("errorPage.notFoundMessage");
      errorPrompt = t("errorPage.notFoundPrompt");
    }
    return (
      <>
        {showHeaderFooter() ? (
          <HeaderComponent
            shouldUseRouter={false}
            recordHeaderHeight={(height: number) => {
              setHeaderHeight(height);
            }}
          />
        ) : null}
        <NotFoundPage
          errorMessage={errorMessage}
          errorPrompt={errorPrompt}
          withoutFooter={!showHeaderFooter()}
        />
      </>
    );
  }
}

export { App };

export default App;

const MaxBodyHeight = 900;
const FooterVisibleHeight = 40;

const Content = styled.div<{ headerHeight: number }>`
  flex: 1 1 auto;
  position: relative;
  min-height: calc(
    ${document.body.clientHeight > MaxBodyHeight
        ? `${MaxBodyHeight}px`
        : `100vh - ${FooterVisibleHeight}px`} -
      ${({ headerHeight }) => headerHeight}px
  );
  ${(props) =>
    props.theme.isMobile === "true" &&
    css`
      min-height: calc(
        ${document.body.clientHeight > MaxBodyHeight
            ? `${MaxBodyHeight}px`
            : `var(--vh, 1vh) * 100 - ${FooterVisibleHeight}px`} -
          ${props.headerHeight}px
      );
    `}
`;
